import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import {
  addNativeRequestListener,
  addNativeResultListener,
  clearAuthSession,
  createNativeRequestId,
  isReactNativeWebView,
  isNativeApp,
  requestAuthSession,
  navigateAppRoute,
  sendToNative,
  storeAuthSession,
} from "@/lib/hiveflowNative";
import { getHiveflowConfig } from "@/lib/environment";
import { GUEST_MODE_KEY, featuresForSession, guestModeStored } from "@/lib/guestMode";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const mobileAuthInFlight = useRef(null);
  const authGeneration = useRef(0);
  const authSessionRequest = useRef(null);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("imker_user")); } catch { return null; }
  });
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("imker_token");
  const hasNativeBridge = typeof window !== "undefined" && isNativeApp();
  const [guestMode, setGuestMode] = useState(() => hasNativeBridge && guestModeStored());
  // Optimistic: if we have a cached user + token, render instantly and validate in background
  const [loading, setLoading] = useState(() => !guestModeStored() && ((!!hasToken && !user) || (!hasToken && hasNativeBridge)));
  const isGuest = Boolean(guestMode && hasNativeBridge && !user);
  const sessionMode = user ? "authenticated" : isGuest ? "guest" : "anonymous";
  const features = featuresForSession(isGuest);

  const debugNativeAuth = useCallback((label, meta = {}) => {
    try {
      const cfg = getHiveflowConfig();
      if (cfg.appEnv === "production") return;
      // Never include token values in this log.
      console.info("[native-auth]", label, meta);
    } catch {}
  }, []);

  const postNativeMessage = useCallback((type, payload = {}) => {
    const bridgeAvailable = isReactNativeWebView();
    debugNativeAuth("bridge", { available: bridgeAvailable, type });
    if (!bridgeAvailable) return false;
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type,
        payload: { requestId: createNativeRequestId(), ...payload },
      }));
      debugNativeAuth("message_sent", { type, sent: true });
      return true;
    } catch {
      debugNativeAuth("message_sent", { type, sent: false });
      return false;
    }
  }, [debugNativeAuth]);

  const sendUnauthenticatedToNative = useCallback((requestId) => {
    const sent = postNativeMessage("MOBILE_AUTH_STATE", { state: "UNAUTHENTICATED", ...(requestId ? { requestId } : {}) });
    debugNativeAuth("auth_state", { state: "UNAUTHENTICATED", sent });
    return sent;
  }, [debugNativeAuth, postNativeMessage]);

  const sendMobileTokenToNative = useCallback(({ token, expiresAt, userId, tenantId, source = "unknown", requestId }) => {
    const expires = Date.parse(expiresAt || "");
    const tokenAvailable = Boolean(token) && Number.isFinite(expires) && expires > Date.now();
    debugNativeAuth("token_ready", { tokenAvailable, source });
    if (!tokenAvailable) return false;
    const sent = postNativeMessage("MOBILE_AUTH_TOKEN", {
      token,
      expiresAt,
      userId,
      tenantId,
      ...(requestId ? { requestId } : {}),
    });
    debugNativeAuth("set_token", { sent, tokenAvailable: true, source });
    return sent;
  }, [debugNativeAuth, postNativeMessage]);

  const syncMobileAuthToNative = useCallback(async (activeUser, options = {}) => {
    const bridgeAvailable = isReactNativeWebView();
    const webToken = typeof window !== "undefined" ? localStorage.getItem("imker_token") : null;
    debugNativeAuth("bootstrap", {
      bridgeAvailable,
      completed: true,
      authenticated: Boolean(activeUser && webToken),
      tokenAvailable: Boolean(webToken),
      reason: options.reason || "unknown",
    });
    if (!bridgeAvailable) return false;
    if (!activeUser || !webToken) {
      sendUnauthenticatedToNative(options.requestId);
      return false;
    }
    const generation = options.generation ?? authGeneration.current;
    const requestKey = `${generation}:${activeUser.id}:${webToken}:${options.requestId || "bootstrap"}`;
    if (mobileAuthInFlight.current?.key === requestKey) return mobileAuthInFlight.current.promise;
    const promise = (async () => {
      try {
        const r = await api.post("/mobile/auth/token", {
          deviceId: options.deviceId || options.device_id,
          appVersion: options.appVersion || options.app_version,
          platform: options.platform,
        });
        const currentUser = (() => {
          try { return JSON.parse(localStorage.getItem("imker_user") || "null"); } catch { return null; }
        })();
        if (
          authGeneration.current !== generation
          || localStorage.getItem("imker_token") !== webToken
          || String(currentUser?.id || "") !== String(activeUser.id)
        ) return false;
        const sent = sendMobileTokenToNative({
          token: r.data?.token,
          expiresAt: r.data?.expiresAt,
          userId: activeUser.id,
          tenantId: activeUser.organization_id || activeUser.id,
          source: "mobile_exchange",
          requestId: options.requestId,
        });
        if (!sent) throw new Error("Mobiele token heeft geen geldige vervaltijd");
        return true;
      } catch (err) {
        const status = err?.response?.status;
        sendToNative(status === 401 ? "AUTH_REQUIRED" : "NATIVE_ERROR", {
          ...(options.requestId ? { requestId: options.requestId } : {}),
          message: err?.response?.data?.detail || "Mobiele authenticatie mislukt",
          status,
        });
        return false;
      }
    })();
    mobileAuthInFlight.current = { key: requestKey, promise };
    try {
      return await promise;
    } finally {
      if (mobileAuthInFlight.current?.key === requestKey) mobileAuthInFlight.current = null;
    }
  }, [debugNativeAuth, sendMobileTokenToNative, sendUnauthenticatedToNative]);

  const applySession = useCallback((data, options = {}) => {
    if (!data?.token || !data?.user) return null;
    if (options.expectedGeneration != null && authGeneration.current !== options.expectedGeneration) return null;
    const generation = authGeneration.current + 1;
    authGeneration.current = generation;
    const upgradingGuest = guestModeStored();
    localStorage.removeItem(GUEST_MODE_KEY);
    setGuestMode(false);
    localStorage.setItem("imker_token", data.token);
    localStorage.setItem("imker_user", JSON.stringify(data.user));
    setUser(data.user);
    setLoading(false);
    if (options.syncNative !== false) {
      storeAuthSession({ token: data.token, user: data.user });
    }
    if (upgradingGuest) {
      postNativeMessage("GUEST_ACCOUNT_UPGRADE", { user: data.user });
    }
    syncMobileAuthToNative(data.user, { reason: options.reason || "apply_session", generation });
    return data.user;
  }, [postNativeMessage, syncMobileAuthToNative]);

  useEffect(() => {
    if (isGuest) {
      postNativeMessage("GUEST_MODE_STARTED", {});
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("imker_token");
    if (!token) {
      sendUnauthenticatedToNative();
      if (!isNativeApp()) setLoading(false);
      return;
    }
    const generation = authGeneration.current;
    api.get("/auth/me")
      .then((r) => {
        if (authGeneration.current !== generation || localStorage.getItem("imker_token") !== token) return;
        setUser(r.data);
        localStorage.setItem("imker_user", JSON.stringify(r.data));
        storeAuthSession({ token, user: r.data });
        syncMobileAuthToNative(r.data, { reason: "auth_bootstrap", generation });
      })
      .catch((err) => {
        if (err?.response?.status === 401 && authGeneration.current === generation && localStorage.getItem("imker_token") === token) {
          authGeneration.current += 1;
          localStorage.removeItem("imker_token");
          localStorage.removeItem("imker_user");
          setUser(null);
          clearAuthSession();
          sendUnauthenticatedToNative();
        }
      })
      .finally(() => setLoading(false));
  }, [isGuest, postNativeMessage, sendUnauthenticatedToNative, syncMobileAuthToNative]);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const stop = addNativeRequestListener(async ({ type, payload }) => {
      if (type !== "REQUEST_MOBILE_AUTH") return;
      const requestId = payload?.requestId || payload?.request_id;
      let activeUser = user;
      if (!activeUser) {
        try { activeUser = JSON.parse(localStorage.getItem("imker_user")); } catch { activeUser = null; }
      }
      if (!activeUser && localStorage.getItem("imker_token")) {
        const generation = authGeneration.current;
        const token = localStorage.getItem("imker_token");
        try {
          const me = await api.get("/auth/me");
          if (authGeneration.current !== generation || localStorage.getItem("imker_token") !== token) return;
          activeUser = me.data;
          localStorage.setItem("imker_user", JSON.stringify(me.data));
          setUser(me.data);
          storeAuthSession({ token, user: me.data });
        } catch (error) {
          if (error?.response?.status === 401 && authGeneration.current === generation && localStorage.getItem("imker_token") === token) {
            authGeneration.current += 1;
            localStorage.removeItem("imker_token");
            localStorage.removeItem("imker_user");
            clearAuthSession();
            setUser(null);
          }
          activeUser = null;
        }
      }
      if (!activeUser) {
        if (guestModeStored()) {
          postNativeMessage("MOBILE_AUTH_STATE", { state: "GUEST", ...(requestId ? { requestId } : {}) });
          return;
        }
        sendUnauthenticatedToNative(requestId);
        sendToNative("AUTH_REQUIRED", { message: "Login vereist", ...(requestId ? { requestId } : {}) });
        return;
      }
      try {
        await syncMobileAuthToNative(activeUser, {
          reason: "native_request",
          deviceId: payload?.deviceId || payload?.device_id,
          appVersion: payload?.appVersion || payload?.app_version,
          platform: payload?.platform,
          requestId,
          generation: authGeneration.current,
        });
      } catch (err) {
        const status = err?.response?.status;
        sendToNative(status === 401 ? "AUTH_REQUIRED" : "NATIVE_ERROR", {
          ...(requestId ? { requestId } : {}),
          message: err?.response?.data?.detail || "Mobiele authenticatie mislukt",
          status,
        });
      }
    });
    return stop;
  }, [postNativeMessage, sendUnauthenticatedToNative, syncMobileAuthToNative, user]);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) setLoading(false);
    }, 1200);
    const requestedGeneration = authGeneration.current;
    const requestedId = createNativeRequestId();
    authSessionRequest.current = { requestId: requestedId, generation: requestedGeneration };
    const stop = addNativeResultListener(({ type, payload, requestId: resultRequestId }) => {
      if (type !== "AUTH_SESSION_RESULT") return;
      const expected = authSessionRequest.current;
      const responseId = resultRequestId || payload?.requestId || payload?.request_id;
      const responseGeneration = payload?.authGeneration ?? payload?.auth_generation;
      if (
        !expected
        || responseId !== expected.requestId
        || authGeneration.current !== expected.generation
        || (responseGeneration != null && Number(responseGeneration) !== expected.generation)
      ) return;
      authSessionRequest.current = null;
      settled = true;
      clearTimeout(timer);
      if (payload?.token && payload?.user) {
        applySession(payload, { syncNative: false, expectedGeneration: expected.generation });
      } else if (payload?.state === "GUEST" || payload?.guest === true) {
        authGeneration.current = expected.generation + 1;
        localStorage.setItem(GUEST_MODE_KEY, "1");
        setGuestMode(true);
        setLoading(false);
      } else {
        setLoading(false);
      }
    });
    requestAuthSession({ requestId: requestedId, authGeneration: requestedGeneration });
    return () => {
      settled = true;
      if (authSessionRequest.current?.requestId === requestedId) authSessionRequest.current = null;
      clearTimeout(timer);
      stop();
    };
  }, [applySession]);

  const login = useCallback(async (email, password, verificationCode = "") => {
    const generation = authGeneration.current + 1;
    authGeneration.current = generation;
    const body = { email, password };
    if (verificationCode) body.verification_code = verificationCode;
    const r = await api.post("/auth/login", body);
    if (r.data?.requires_verification) return r.data;
    return applySession(r.data, { reason: "login", expectedGeneration: generation });
  }, [applySession]);

  const register = useCallback(async (name, email, password) => {
    const r = await api.post("/auth/register", { name, email, password });
    return r.data;
  }, []);

  const verifyEmail = useCallback(async ({ email, code, token }) => {
    const generation = authGeneration.current + 1;
    authGeneration.current = generation;
    const r = await api.post("/auth/verify-email", { email, code, token });
    return applySession(r.data, { reason: "verify_email", expectedGeneration: generation });
  }, [applySession]);

  const requestMagicLink = useCallback(async (email) => {
    const r = await api.post("/auth/magic-link/request", { email });
    return r.data;
  }, []);

  const consumeMagicLink = useCallback(async ({ email, code, token }) => {
    const generation = authGeneration.current + 1;
    authGeneration.current = generation;
    const r = await api.post("/auth/magic-link/consume", { email, code, token });
    return applySession(r.data, { reason: "magic_login", expectedGeneration: generation });
  }, [applySession]);

  const requestPasswordReset = useCallback(async (email) => {
    const r = await api.post("/auth/password-reset/request", { email });
    return r.data;
  }, []);

  const validatePasswordReset = useCallback(async ({ email, token }) => {
    const r = await api.post("/auth/password-reset/validate", { email, token });
    return r.data;
  }, []);

  const confirmPasswordReset = useCallback(async ({ email, token, password }) => {
    const r = await api.post("/auth/password-reset/confirm", { email, token, password });
    return r.data;
  }, []);

  const continueAsGuest = useCallback(() => {
    if (!isNativeApp()) return false;
    authGeneration.current += 1;
    localStorage.removeItem("imker_token");
    localStorage.removeItem("imker_user");
    localStorage.setItem(GUEST_MODE_KEY, "1");
    setUser(null);
    setGuestMode(true);
    setLoading(false);
    clearAuthSession();
    postNativeMessage("GUEST_MODE_STARTED", {});
    return true;
  }, [postNativeMessage]);

  const exitGuest = useCallback(() => {
    localStorage.removeItem(GUEST_MODE_KEY);
    setGuestMode(false);
    postNativeMessage("GUEST_MODE_ENDED", {});
  }, [postNativeMessage]);

  const refreshUser = useCallback(async () => {
    const generation = authGeneration.current;
    const token = localStorage.getItem("imker_token");
    const r = await api.get("/auth/me");
    if (authGeneration.current !== generation || localStorage.getItem("imker_token") !== token) return null;
    localStorage.setItem("imker_user", JSON.stringify(r.data));
    setUser(r.data);
    storeAuthSession({ token, user: r.data });
    return r.data;
  }, []);

  const logout = () => {
    authGeneration.current += 1;
    if (guestModeStored()) exitGuest();
    const cleared = postNativeMessage("CLEAR_MOBILE_AUTH_TOKEN", {});
    debugNativeAuth("clear_token", { sent: cleared });
    localStorage.removeItem("imker_token");
    localStorage.removeItem("imker_user");
    clearAuthSession();
    setUser(null);
    navigateAppRoute("/welcome", { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, loading, isGuest, sessionMode, features, login, register, verifyEmail, requestMagicLink, consumeMagicLink, requestPasswordReset, validatePasswordReset, confirmPasswordReset, continueAsGuest, exitGuest, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
